import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismInput } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { timeout } from 'lumen/utils/waiters';
import { localCopy } from 'tracked-toolbox';

interface SettingsCentralMappingTablesCellsEditableTextCellSignature {
  Args: {
    cellDataTestId: string;
    isMixed?: boolean;
    isReadOnly?: boolean;
    maxLength?: number;
    setValue: (value: string) => void;
    showTooltip?: boolean;
    value: string;
  };
}

export default class SettingsCentralMappingTablesCellsEditableTextCell extends Component<SettingsCentralMappingTablesCellsEditableTextCellSignature> {
  @tracked isEditing = false;

  @localCopy('args.value') declare tempValue: string;

  onBlurTask = task({ restartable: true }, async () => {
    await timeout('extra-short');

    // Early return if the value has not changed.
    // For bulk action bar, to avoid selected rows being changed to the tempValue, which could be null.
    if (this.tempValue === this.args.value) {
      this.isEditing = false;
      return;
    }

    this.setTextValue();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get inEditMode() {
    return this.isReadOnly === false && this.isEditing;
  }

  get isReadOnly(): boolean {
    return this.args.isReadOnly === true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxLength() {
    return this.args.maxLength ?? 250;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showEditButton() {
    return this.isReadOnly === false && !this.isEditing;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tempValueShortened() {
    if (this.tempValueTrimmed.length > this.maxLength) {
      return this.tempValueTrimmed.slice(0, this.maxLength);
    }

    return this.tempValueTrimmed;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tempValueTrimmed() {
    return this.tempValue.trim();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setTextValue() {
    // Fix for `onKeyDownValue`: when pressing Enter, `setTextValue` is called.
    // However, the `onBlur` of the input also triggers, which also calls `setTextValue`.
    // To avoid it being executed twice and Ember complaining about the `isEditing` property
    // being changed twice in the same run loop, we return early if the `setTextValue` has already been called.
    if (!this.isEditing) {
      return;
    }

    this.args.setValue(this.tempValueShortened);

    this.isEditing = false;
  }

  @action onClickEdit(): void {
    this.isEditing = true;
    this.tempValue = this.args.value ?? '';
  }

  @action onInputValue(event: Event): void {
    this.tempValue = (event.target as HTMLInputElement).value;
  }

  @action onKeyDownValue(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.setTextValue();
    }
  }

  <template>
    <div
      class="central-mapping__table-mapping__td-text flex flex-row items-center gap-300"
      data-test-id={{@cellDataTestId}}
    >
      {{#if this.inEditMode}}
        <PrismInput
          @autofocus={{true}}
          @maxLength={{this.maxLength}}
          @onBlur={{this.onBlurTask.perform}}
          @onInput={{this.onInputValue}}
          @onKeyDown={{this.onKeyDownValue}}
          @removePadding={{true}}
          @type="text"
          @value={{this.tempValue}}
          data-test-id="central-mapping-td-text-input"
        />
      {{else}}
        <div
          class="text-200--regular overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{#if @value}}
            {{@value}}

            {{#if @showTooltip}}
              <EmberTooltip
                @side="bottom"
                @tooltipClass="default-tooltip default-tooltip--large"
              >
                <p class="text-200--regular">{{@value}}</p>
              </EmberTooltip>
            {{/if}}
          {{else}}
            {{#if @isMixed}}
              {{t "settings.central_mapping.mapping.mixed"}}
            {{else}}
              {{t "general.missing"}}
            {{/if}}
          {{/if}}
        </div>
      {{/if}}

      {{#if this.showEditButton}}
        <div class="central-mapping__table-mapping__td-text__button-edit">
          <PrismButton
            @buttonType="ghost"
            @icon="edit-pencil"
            @size="small"
            data-test-id="central-mapping-td-text-edit-button"
            {{on "click" this.onClickEdit}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
